import {
  Accordion,
  AccordionItem,
  AccordionItemBody,
  AccordionItemHeader,
  ArrowLeftIcon,
  Badge,
  Box,
  Button,
  Heading,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PlusIcon,
  useToast,
} from '@razorpay/blade/components';
import gradientGray from 'assets/mss/gradient-gray.svg';
import { SalaryComponent, SalaryComponentCategory } from 'components/SalaryComponents/types';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AddComponentModal, EarningsSection, OthersSection, PreviewCtc } from './components';
import { useSalaryStructure } from './hooks/useSalaryStructure';
import { useSalaryStructureComponents } from './hooks/useSalaryStructureComponents';

const DefineStructureStep = () => {
  const history = useHistory();
  const toast = useToast();

  // Custom hooks for component and structure management
  const { allComponents, isLoading: isComponentsLoading } = useSalaryStructureComponents();

  const {
    formData,
    removeComponentById,
    earningComponents,
    variableComponents,
    nonPayableBenefitsComponents,
    salaryStructureComponents,
    updateComponentById,
    addComponentsByIds,
    setEmployerContributions,
    employerContributions,
    removeContributionById,
  } = useSalaryStructure();

  const { setCurrentStep } = useWizardInfo();

  // Modal states
  const [isAddComponentModalOpen, setIsAddComponentModalOpen] = useState(false);
  const [isAddContributionModalOpen, setIsAddContributionModalOpen] = useState(false);

  // Get current selected component IDs
  const selectedComponentIds = salaryStructureComponents.map((comp) => comp.salaryComponent.id);
  const selectedContributionIds = employerContributions.map((comp) => comp.componentId);

  // Handlers
  const handleAddComponent = (type: SalaryComponentCategory) => {
    setIsAddComponentModalOpen(true);
  };

  const handleAddComponents = (components: SalaryComponent[]) => {
    addComponentsByIds(
      components.map((component) => component.id),
      components,
    );
  };

  const handleCreateStructure = () => {
    // Call API
    console.log('Creating structure:', salaryStructureComponents);

    toast.show({
      color: 'positive',
      content: 'Salary structure created successfully',
    });

    // Navigate back to the list view
    history.push('/salary-structures');
  };

  const handleBack = () => {
    setCurrentStep(0);
  };

  return (
    <>
      <ModalHeader title={formData.name} subtitle={formData.description}></ModalHeader>
      <ModalBody height="100%" padding="spacing.0">
        <Box
          padding="spacing.6"
          display="flex"
          gap="spacing.7"
          height="100%"
          backgroundImage={`url("${gradientGray}")`}
          backgroundRepeat="no-repeat"
          backgroundSize="contain"
          backgroundPosition="right bottom">
          <Box
            width="100%"
            height="100%"
            flex="1"
            display="flex"
            flexDirection="column"
            justifyContent="stretch"
            overflow="auto">
            <Box display="flex" justifyContent="space-between">
              <Heading color="surface.text.gray.subtle">Build your structure</Heading>
              <Box display="flex" gap="spacing.4" alignItems="center">
                <Button
                  variant="secondary"
                  onClick={() => handleAddComponent('EARNINGS_CATEGORY')}
                  icon={PlusIcon}
                  isLoading={isComponentsLoading}>
                  Add components
                </Button>
                {/* <Button
                  variant="tertiary"
                  onClick={() => setIsAddContributionModalOpen(true)}
                  icon={PlusIcon}
                  isLoading={isComponentsLoading}>
                  Add contribution
                </Button> */}
              </Box>
            </Box>
            <Box marginTop="spacing.5" borderWidth="thinner" borderRadius="medium" overflow="clip">
              <Accordion maxWidth="100%" variant="filled" defaultExpandedIndex={0}>
                <AccordionItem>
                  <AccordionItemHeader
                    title="Earning"
                    titleSuffix={
                      <Badge color={earningComponents.length ? 'primary' : 'neutral'}>
                        {earningComponents.length.toString()}
                      </Badge>
                    }
                  />
                  <AccordionItemBody>
                    <EarningsSection
                      components={earningComponents}
                      onDeleteComponent={(id) => removeComponentById(id)}
                      onUpdateComponent={(id, component) => updateComponentById(id, component)}
                    />
                  </AccordionItemBody>
                </AccordionItem>
              </Accordion>
              <Accordion maxWidth="100%" variant="filled">
                <AccordionItem>
                  <AccordionItemHeader
                    title="Variable"
                    titleSuffix={
                      <Badge color={variableComponents.length ? 'primary' : 'neutral'}>
                        {variableComponents.length.toString()}
                      </Badge>
                    }
                  />
                  <AccordionItemBody>
                    <EarningsSection
                      components={variableComponents}
                      onDeleteComponent={(id) => removeComponentById(id)}
                      onUpdateComponent={(id, component) => updateComponentById(id, component)}
                    />
                  </AccordionItemBody>
                </AccordionItem>
              </Accordion>
              <Accordion maxWidth="100%" variant="filled">
                <AccordionItem>
                  <AccordionItemHeader
                    title="Non-payable benefits"
                    titleSuffix={
                      <Badge color={nonPayableBenefitsComponents.length ? 'primary' : 'neutral'}>
                        {nonPayableBenefitsComponents.length.toString()}
                      </Badge>
                    }
                  />
                  <AccordionItemBody>
                    <OthersSection
                      components={nonPayableBenefitsComponents}
                      contributions={employerContributions}
                      onDeleteComponent={(id) => removeComponentById(id)}
                      onUpdateComponent={(id, component) => updateComponentById(id, component)}
                      onDeleteContribution={(id) => removeContributionById(id)}
                    />
                  </AccordionItemBody>
                </AccordionItem>
              </Accordion>
            </Box>
          </Box>

          <Box width="calc(max(30%, 400px))" height="100%" overflow="auto">
            <PreviewCtc />
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="space-between">
          <Button variant="tertiary" onClick={handleBack} icon={ArrowLeftIcon} iconPosition="left">
            Back: Name & description
          </Button>

          <Button variant="primary" onClick={handleCreateStructure} isDisabled={false}>
            Create structure
          </Button>
        </Box>
      </ModalFooter>

      {/* Modals */}
      <AddComponentModal
        isOpen={isAddComponentModalOpen}
        onClose={() => setIsAddComponentModalOpen(false)}
        availableComponents={allComponents}
        selectedComponentIds={selectedComponentIds}
        onAddComponents={handleAddComponents}
      />

      {/* <AddContributionModal
        isOpen={isAddContributionModalOpen}
        onClose={() => setIsAddContributionModalOpen(false)}
        selectedContributionIds={selectedContributionIds}
        onAddContributions={(employerContributions) => {
          setEmployerContributions(employerContributions);
        }}
      /> */}
    </>
  );
};

export default DefineStructureStep;
